import { FocusTrap } from 'focus-trap-react';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';

import {
  ViewWrapper,
  ViewWrapperDefaultGrid,
} from '@xing-com/platform-layout-view-wrapper';
import { Portal } from '@xing-com/portal';

import { CloseButton } from './close-button';
import * as Styled from './modal-page.styles';
import type { ModalPageProps } from './modal-page.types';

const { modalPageDuration, modalPageTransitionName } = Styled;

export const ModalPage = React.forwardRef<HTMLDivElement, ModalPageProps>(
  (
    {
      children,
      closeButton,
      contentClassName,
      isAnimated,
      isShown,
      noGrid,
      noMargin,
      ...props
    },
    forwardedRef
  ): JSX.Element => {
    const modalRef = React.useRef<HTMLDivElement>(null);
    const resolvedRef = forwardedRef || modalRef;

    // Focus management
    React.useEffect(() => {
      if (isShown && isAnimated) {
        // Store the currently focused element to return to later
        const previouslyFocused = document.activeElement;

        // Focus the modal after it's rendered
        const focusTimer = setTimeout(() => {
          if (typeof resolvedRef !== 'function' && resolvedRef.current) {
            resolvedRef.current.focus({ preventScroll: true });
          }
        }, 100);

        return () => {
          clearTimeout(focusTimer);
          if (previouslyFocused instanceof HTMLElement) {
            previouslyFocused.focus();
          }
        };
      }

      return undefined;
    }, [isShown, isAnimated, resolvedRef]);

    // Prevent background scrolling
    React.useEffect(() => {
      if (isShown && isAnimated) {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
          document.body.style.overflow = originalOverflow;
        };
      }

      return undefined;
    }, [isShown, isAnimated]);

    // Keyboard handling
    React.useEffect(() => {
      const handleKey = (event: KeyboardEvent): void => {
        if (isShown && closeButton && event.key === 'Escape') {
          event.preventDefault();
          closeButton.onClick?.();
        }
      };

      document.addEventListener('keydown', handleKey);

      return () => {
        document.removeEventListener('keydown', handleKey);
      };
    }, [closeButton, isShown]);

    const content = (
      <ViewWrapper
        contentClassName={contentClassName}
        data-xds="ModalPage"
        {...props}
      >
        {closeButton ? <CloseButton {...closeButton} /> : undefined}
        <Styled.Container
          $noMargin={noMargin}
          aria-modal={isAnimated ? 'true' : undefined}
          ref={resolvedRef}
          role={isAnimated ? 'dialog' : undefined}
          tabIndex={isAnimated ? -1 : undefined}
        >
          {children}
        </Styled.Container>
      </ViewWrapper>
    );

    const wrappedContent = noGrid ? (
      content
    ) : (
      <ViewWrapperDefaultGrid>{content}</ViewWrapperDefaultGrid>
    );

    if (isAnimated) {
      return (
        <Portal>
          <CSSTransition
            appear={isShown}
            classNames={modalPageTransitionName}
            in={isShown}
            key="modalPage"
            mountOnEnter
            timeout={parseInt(modalPageDuration)}
            unmountOnExit
          >
            <Styled.AnimatedModalPage>
              <FocusTrap
                active={isShown}
                focusTrapOptions={{
                  escapeDeactivates: Boolean(closeButton),
                  returnFocusOnDeactivate: true,
                }}
              >
                {wrappedContent}
              </FocusTrap>
            </Styled.AnimatedModalPage>
          </CSSTransition>
        </Portal>
      );
    }

    return wrappedContent;
  }
);

ModalPage.displayName = 'ModalPage';
